/**
 * WeRemote API client.
 *
 * Features:
 *   - Injects Authorization + X-Org-Slug on every request
 *   - Auto-refresh on 401 (one retry per request, max one concurrent refresh)
 *   - Typed errors via ApiError
 *   - Single point to swap fetch impl (e.g. for SSR or tests)
 *
 * Usage:
 *   const users = await api.get<PaginatedUsers>('/users');
 *   const created = await api.post<User>('/users', { email, password, role });
 */

import { useAuthStore } from '@/lib/auth-store';

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public issues?: unknown,
    public requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// In-flight refresh promise to coalesce concurrent 401s
let refreshInFlight: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const { refreshToken, clear, updateTokens } = useAuthStore.getState();
    if (!refreshToken) return false;

    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) {
        clear();
        return false;
      }
      const data = (await res.json()) as { accessToken: string; refreshToken: string };
      updateTokens(data.accessToken, data.refreshToken);
      return true;
    } catch {
      clear();
      return false;
    } finally {
      // Release in next microtask so coalesced waiters all see the result
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();

  return refreshInFlight;
}

interface RequestOpts {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
  /** Skip auth header (used by /auth/login). */
  noAuth?: boolean;
  /** Override Accept; defaults to application/json. */
  accept?: string;
}

function buildUrl(path: string, query?: RequestOpts['query']): string {
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.pathname + url.search;
}

async function request<T>(path: string, opts: RequestOpts = {}, retried = false): Promise<T> {
  const { accessToken, orgSlug } = useAuthStore.getState();

  const headers: Record<string, string> = {
    Accept: opts.accept ?? 'application/json',
  };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (!opts.noAuth && accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (orgSlug) headers['X-Org-Slug'] = orgSlug;

  const res = await fetch(buildUrl(path, opts.query), {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  // 401 → try refresh once, then retry the original request
  if (res.status === 401 && !retried && !opts.noAuth) {
    const refreshed = await refreshTokens();
    if (refreshed) {
      return request<T>(path, opts, true);
    }
  }

  if (res.status === 204) {
    return undefined as T;
  }

  // Non-JSON responses (e.g. CSV exports)
  if (opts.accept && !opts.accept.includes('application/json')) {
    if (!res.ok) {
      throw new ApiError(res.status, 'NonOk', `Request failed: ${res.status}`);
    }
    return (await res.text()) as unknown as T;
  }

  let payload: unknown;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }

  if (!res.ok) {
    const errBody = payload as
      | { error?: string; message?: string; issues?: unknown; requestId?: string }
      | null;
    throw new ApiError(
      res.status,
      errBody?.error ?? 'UnknownError',
      errBody?.message ?? `Request failed: ${res.status}`,
      errBody?.issues,
      errBody?.requestId,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, opts: Omit<RequestOpts, 'method' | 'body'> = {}) =>
    request<T>(path, { ...opts, method: 'GET' }),

  post: <T>(path: string, body?: unknown, opts: Omit<RequestOpts, 'method'> = {}) =>
    request<T>(path, { ...opts, method: 'POST', body }),

  patch: <T>(path: string, body?: unknown, opts: Omit<RequestOpts, 'method'> = {}) =>
    request<T>(path, { ...opts, method: 'PATCH', body }),

  put: <T>(path: string, body?: unknown, opts: Omit<RequestOpts, 'method'> = {}) =>
    request<T>(path, { ...opts, method: 'PUT', body }),

  delete: <T>(path: string, opts: Omit<RequestOpts, 'method' | 'body'> = {}) =>
    request<T>(path, { ...opts, method: 'DELETE' }),

  /** Download a CSV (or other text) as a Blob for browser download. */
  downloadText: async (path: string, query?: RequestOpts['query']): Promise<string> => {
    return request<string>(path, { method: 'GET', query, accept: 'text/csv' });
  },
};
